import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  FileText, 
  ExternalLink, 
  TrendingUp, 
  AlertCircle,
  Calendar,
  Clock,
  CheckCircle
} from 'lucide-react';
import { ApiRoutes } from '../../../api/routes';

interface BillingSnapshot {
  status: 'published' | 'none';
  cycleDate?: string;
  lastActual?: {
    period: string;
    total: number;
    breakdown: Array<{
      item: string;
      amount: number;
      type: 'hourly' | 'milestone' | 'saas';
    }>;
  };
  current?: {
    period: string;
    total: number;
    breakdown: Array<{
      item: string;
      amount: number;
      type: 'hourly' | 'milestone' | 'saas';
      isChangeOrder?: boolean;
    }>;
  };
  next?: {
    period: string;
    total: number;
    breakdown: Array<{
      item: string;
      amount: number;
      type: 'hourly' | 'milestone' | 'saas';
    }>;
  };
  invoices?: Array<{
    id: string;
    number: string;
    amount: number;
    sentDate: string;
    sharepointUrl?: string;
  }>;
}

interface BillingManagementProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const BillingManagement: React.FC<BillingManagementProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [billingSnapshot, setBillingSnapshot] = useState<BillingSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBillingSnapshot();
  }, [projectId]);

  const loadBillingSnapshot = async () => {
    try {
      setLoading(true);
      const response = await ApiRoutes.getBillingSnapshot(projectId);
      if (response.success && response.data) {
        setBillingSnapshot(response.data);
      } else {
        console.error('Failed to load billing snapshot:', response.error);
        setBillingSnapshot({ status: 'none' });
      }
    } catch (error) {
      console.error('Error loading billing snapshot:', error);
      setBillingSnapshot({ status: 'none' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hourly':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'milestone':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'saas':
        return <TrendingUp className="w-4 h-4 text-purple-500" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4267A1] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading billing information...</p>
        </div>
      </div>
    );
  }

  if (!billingSnapshot || billingSnapshot.status === 'none') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Published Billing Available</h3>
          <p className="text-gray-600 mb-4">
            Billing information for this project hasn't been published yet. 
            Please contact your Customer Success Manager for current billing details.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              <strong>Need billing information?</strong><br />
              Reach out to your Tyler Technologies project team or contact customer support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Billing Management
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Published billing snapshots and invoice management
          </p>
          
          {/* Watermark */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="w-4 h-4 mr-2" />
              <span>
                As of last billing cycle: <strong>{formatDate(billingSnapshot.cycleDate!)}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* 90-Day Tray */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Last Actual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tyler-card border-l-4 border-green-500"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 text-green-500 mr-2" />
                Last (Actual)
              </h3>
              <div className="text-3xl font-bold text-green-600 mb-4">
                {formatCurrency(billingSnapshot.lastActual?.total || 0)}
              </div>
              <div className="text-sm text-gray-500 mb-4">
                {billingSnapshot.lastActual?.period}
              </div>
              <div className="space-y-2">
                {billingSnapshot.lastActual?.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      {getTypeIcon(item.type)}
                      <span className="ml-2 text-gray-700">{item.item}</span>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Current */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="tyler-card border-l-4 border-blue-500"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 text-blue-500 mr-2" />
                Current
              </h3>
              <div className="text-3xl font-bold text-blue-600 mb-4">
                {formatCurrency(billingSnapshot.current?.total || 0)}
              </div>
              <div className="text-sm text-gray-500 mb-4">
                {billingSnapshot.current?.period}
              </div>
              <div className="space-y-2">
                {billingSnapshot.current?.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      {getTypeIcon(item.type)}
                      <span className={`ml-2 ${item.isChangeOrder ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                        {item.item}
                        {item.isChangeOrder && (
                          <span className="ml-1 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                            Change Order
                          </span>
                        )}
                      </span>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Next Forecast */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="tyler-card border-l-4 border-purple-500"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 text-purple-500 mr-2" />
                Next (Forecast)
              </h3>
              <div className="text-3xl font-bold text-purple-600 mb-4">
                {formatCurrency(billingSnapshot.next?.total || 0)}
              </div>
              <div className="text-sm text-gray-500 mb-4">
                {billingSnapshot.next?.period}
              </div>
              <div className="space-y-2">
                {billingSnapshot.next?.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      {getTypeIcon(item.type)}
                      <span className="ml-2 text-gray-700">{item.item}</span>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Invoices */}
        {billingSnapshot.invoices && billingSnapshot.invoices.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="tyler-card"
          >
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 text-[#4267A1] mr-2" />
                Invoices
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sent Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingSnapshot.invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.number}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.amount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(invoice.sentDate)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.sharepointUrl ? (
                            <a
                              href={invoice.sharepointUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#4267A1] hover:bg-[#2E4F7A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4267A1] transition-colors"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Open PDF
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">PDF not available</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BillingManagement;